import React from 'react';
import AuditLabel from './AuditLabel';
import { formatDate, formatTime } from '../utils/dateUtils';
import { CHECKOUT_MOODS } from '../utils/daycareUtils';

const DaycareVisitCard = ({ visit, onCheckOut, onEdit, onDelete }) => {
    const getStatusInfo = () => {
        if (visit.checkOutTime) {
            return {
                status: 'completed',
                label: 'Completado',
                class: 'status-badge completed'
            };
        } else if (visit.checkInTime) {
            return {
                status: 'active',
                label: 'Activo',
                class: 'status-badge active'
            };
        } else {
            return {
                status: 'pending',
                label: 'Pendiente',
                class: 'status-badge pending'
            };
        }
    };

    const calculateDuration = () => {
        if (!visit.checkInTime) return null;
        
        const checkIn = new Date(visit.checkInTime);
        const checkOut = visit.checkOutTime ? new Date(visit.checkOutTime) : new Date();
        const duration = Math.floor((checkOut - checkIn) / (1000 * 60 * 60)); // Hours
        
        if (duration < 1) {
            const minutes = Math.floor((checkOut - checkIn) / (1000 * 60));
            return `${minutes} min`;
        }
        
        const hours = Math.floor(duration);
        const minutes = Math.floor(((checkOut - checkIn) / (1000 * 60)) % 60);
        
        if (minutes === 0) {
            return `${hours}h`;
        }
        return `${hours}h ${minutes}m`;
    };

    const statusInfo = getStatusInfo();
    const duration = calculateDuration();
    const ownerNames = visit.dog?.owners?.map(owner => owner.name).join(', ') || 'Propietario desconocido';

    const canCheckOut = () => {
        return visit.checkInTime && !visit.checkOutTime;
    };

    const canEdit = () => {
        return true; // Allow editing notes, etc.
    };

    const canDelete = () => {
        return !visit.checkInTime || visit.checkOutTime; // Can delete if not checked in or already completed
    };

    return (
        <div className="daycare-visit-card">
            <div className="visit-header">
                {/* Dog Profile Picture */}
                {(visit.dog?.picture || visit.dog?.pictureUrl) && (
                    <div className="dog-picture-small">
                        <img 
                            src={visit.dog.pictureUrl || visit.dog.picture}
                            alt={visit.dog?.name || 'Dog'}
                            className="dog-picture-img-small"
                            onError={(e) => {
                                // Hide image if it fails to load
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                )}
                
                <div className="visit-header-content">
                    <h3>🌸 {visit.dog?.name || 'Perro desconocido'}</h3>
                    <span className={statusInfo.class}>
                        {statusInfo.label}
                    </span>
                    {visit.checkoutMood && visit.checkOutTime && (() => {
                        const m = CHECKOUT_MOODS.find(x => x.value === visit.checkoutMood);
                        if (!m) return null;
                        return (
                            <span className="co-mood-badge" style={{ background: m.bg, borderColor: m.border, color: m.color, marginLeft: 8 }}>
                                <span className="co-mood-emoji">{m.emoji}</span> {m.label}
                            </span>
                        );
                    })()}
                </div>
            </div>
            
            <div className="visit-info">
                <p className="dog-name">Perro: {visit.dog?.name || 'Desconocido'}</p>
                <p className="dog-breed">Raza: {visit.dog?.breed || 'Desconocida'}</p>
                <p className="owner-names">Propietario(s): {ownerNames}</p>
                <p className="visit-date">Fecha: {formatDate(visit.date || visit.checkInTime)}</p>

                {visit.checkInTime && (
                    <p className="checkin-time">Entrada: {formatTime(visit.checkInTime)}</p>
                )}

                {visit.checkOutTime && (
                    <p className="checkout-time">Salida: {formatTime(visit.checkOutTime)}</p>
                )}

                {duration && (
                    <p className="duration">Duración: {duration}</p>
                )}
                
            </div>
            
            {visit.checkoutMood && (
                <div className="visit-mood">
                    {(() => {
                        const m = CHECKOUT_MOODS.find(x => x.value === visit.checkoutMood);
                        if (!m) return null;
                        return (
                            <span className="co-mood-badge" style={{ background: m.bg, borderColor: m.border, color: m.color }}>
                                <span className="co-mood-emoji">{m.emoji}</span> {m.label}
                            </span>
                        );
                    })()}
                    {visit.checkoutBehaviorNotes && (
                        <p className="co-behavior-text" style={{ marginTop: 8, fontSize: '0.88rem', color: '#374151', lineHeight: 1.4 }}>
                            {visit.checkoutBehaviorNotes}
                        </p>
                    )}
                </div>
            )}

            {visit.notes && (
                <div className="visit-notes">
                    <strong>Notas:</strong> {visit.notes}
                </div>
            )}

            {visit.package && (
                <div className="package-info">
                    <h4>📦 Detalles del paquete</h4>
                    <p><strong>Tipo:</strong> {visit.package.packageType}</p>
                    <p><strong>Días restantes:</strong> {visit.package.daysLeft}</p>
                    {visit.package.expiresAt && (
                        <p><strong>Vence:</strong> {formatDate(visit.package.expiresAt)}</p>
                    )}
                </div>
            )}
            
            {/* Audit Trail */}
            <AuditLabel
                createdBy={visit.createdBy}
                updatedBy={visit.updatedBy}
                createdAt={visit.createdAt}
                updatedAt={visit.updatedAt}
                size="small"
            />
            
            <div className="card-actions">
                {canCheckOut() && onCheckOut && (
                    <button
                        onClick={() => onCheckOut(visit)}
                        className="checkout-btn"
                        title="Registrar salida"
                    >
                        Salida
                    </button>
                )}

                {canEdit() && onEdit && (
                    <button
                        onClick={() => onEdit(visit)}
                        className="edit-btn"
                        title="Editar detalles de visita"
                    >
                        Editar
                    </button>
                )}

                {canDelete() && onDelete && (
                    <button
                        onClick={() => onDelete(visit._id)}
                        className="delete-btn"
                        title="Eliminar visita"
                    >
                        Eliminar
                    </button>
                )}
            </div>
        </div>
    );
};

export default DaycareVisitCard;